/**
 * CDN entry — self-contained IIFE build. Embed as a single script tag,
 * configured via data attributes. No bundler, no npm install on the
 * consumer side, so it works on hosts that can't reach the private registry.
 *
 * ```html
 * <script
 *   src="https://cdn.bytax.de/feedback/v0.1.0/feedback.js"
 *   data-endpoint="https://feedback.bytax.de"
 *   data-token="fbk_…"
 *   defer
 * ></script>
 * ```
 *
 * Optional data attributes: data-locale="en", data-screenshot="false",
 * data-z-index="99999", data-metadata='{"appVersion":"1.2.0"}'.
 *
 * Also exposes a global for programmatic use:
 *   window.bytaxFeedback.initFeedback({ endpoint, token, … })
 */
import { type FeedbackController, type FeedbackOptions, initFeedback } from './index';

// Captured synchronously at load: document.currentScript is the executing
// classic script during top-level evaluation (null in module scripts, which
// this IIFE build is not).
const currentScript =
  document.currentScript instanceof HTMLScriptElement ? document.currentScript : null;

function parseMetadata(raw: string | undefined): Record<string, string> | undefined {
  if (!raw) return undefined;
  try {
    const parsed = JSON.parse(raw) as unknown;
    if (parsed && typeof parsed === 'object') {
      const out: Record<string, string> = {};
      for (const [key, value] of Object.entries(parsed)) {
        if (typeof value === 'string') out[key] = value;
      }
      return Object.keys(out).length > 0 ? out : undefined;
    }
  } catch {
    // ignore malformed metadata
  }
  return undefined;
}

function autoInit(): void {
  const data = currentScript?.dataset;
  if (!data) return;
  const { endpoint, token } = data;
  if (!endpoint || !token) {
    console.warn('[bytax-feedback] script tag is missing data-endpoint and/or data-token');
    return;
  }

  const zIndex = Number(data.zIndex);
  const metadata = parseMetadata(data.metadata);

  initFeedback({
    endpoint,
    token,
    ...(data.locale === 'en' ? { locale: 'en' as const } : {}),
    ...(data.screenshot === 'false' ? { screenshot: false } : {}),
    ...(Number.isFinite(zIndex) && zIndex > 0 ? { zIndex } : {}),
    ...(metadata ? { metadata } : {}),
  });
}

autoInit();

export type { FeedbackController, FeedbackOptions };
export { initFeedback };
